import React, {
  useState, useMemo, useEffect,
} from 'react';
import { useQuery } from '@apollo/client';
import {
  Grid, Header, Segment, Message, Loader, Icon,
} from 'semantic-ui-react';
import { PROJECT_REPORT } from '../../../queries/project';
import SectionToc from './SectionToc';
import BlockRenderer from './BlockRenderer';
import ReferencePanel from './ReferencePanel';

const ProjectReport = ({ projectId }) => {
  const { data, loading, error } = useQuery(PROJECT_REPORT, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);
  const [pendingScrollBlockId, setPendingScrollBlockId] = useState(null);

  const aiReport = data?.project?.aiReport;
  const sections = useMemo(
    () => [...(aiReport?.sections || [])].sort((a, b) => a.order - b.order),
    [aiReport],
  );
  const references = aiReport?.references || [];

  const activeSectionId = selectedSectionId
    || (sections.length > 0 ? sections[0].id : null);
  const activeSection = sections.find((section) => section.id === activeSectionId);
  const selectedReference = references.find((r) => r.id === selectedReferenceId) || null;

  const handleSelectSubheading = (sectionId, blockId) => {
    setSelectedSectionId(sectionId);
    setPendingScrollBlockId(blockId);
  };

  useEffect(() => {
    if (!pendingScrollBlockId) return;
    const element = document.getElementById(`report-block-${pendingScrollBlockId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setPendingScrollBlockId(null);
  }, [activeSection, pendingScrollBlockId]);

  if (loading) {
    return <Loader active inline="centered" content="Loading report..." />;
  }

  if (error) {
    return (
      <Segment>
        <Message negative>
          <Message.Header>Failed to load report</Message.Header>
          <p>{error.message}</p>
        </Message>
      </Segment>
    );
  }

  if (aiReport?.generationStatus && aiReport.generationStatus !== 'completed') {
    if (aiReport.generationStatus === 'failed') {
      return (
        <Segment>
          <Message negative>
            <Message.Header>Report generation failed</Message.Header>
            <p>{aiReport.generationError || 'An error occurred while generating the report.'}</p>
          </Message>
        </Segment>
      );
    }

    return (
      <Segment>
        <Message icon info>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Generating project report&hellip;</Message.Header>
            This may take several minutes. Refresh the page to check for updates.
          </Message.Content>
        </Message>
      </Segment>
    );
  }

  if (sections.length === 0) {
    return (
      <Segment>
        <Message content="No report is available for this project yet." />
      </Segment>
    );
  }

  return (
    <Grid container stackable id="project-report">
      <Grid.Column width={3}>
        <div style={{
          position: 'sticky', top: '1em', maxHeight: 'calc(100vh - 2em)', overflowY: 'auto',
        }}
        >
          <SectionToc
            sections={sections}
            selectedSectionId={activeSectionId}
            onSelectSection={setSelectedSectionId}
            onSelectSubheading={handleSelectSubheading}
          />
        </div>
      </Grid.Column>
      <Grid.Column width={10}>
        {activeSection && (
          <>
            <Header as="h2">{activeSection.title}</Header>
            {activeSection.blocks.map((block) => (
              <BlockRenderer
                key={block.id}
                block={block}
                onReferenceClick={setSelectedReferenceId}
              />
            ))}
          </>
        )}
      </Grid.Column>
      <Grid.Column width={3}>
        <div style={{
          position: 'sticky', top: '1em', maxHeight: 'calc(100vh - 2em)', overflowY: 'auto',
        }}
        >
          <ReferencePanel projectId={projectId} reference={selectedReference} />
        </div>
      </Grid.Column>
    </Grid>
  );
};

export default ProjectReport;
